/**
 * React hook for managing user preferences with Stytch untrusted metadata
 * Now powered by TanStack Query for optimistic updates
 */

import { useCallback } from 'react'
import {
	useUserPreferencesQuery,
	useUpdatePreferenceMutation,
	useUpdateViewPreferenceMutation,
} from '@/queries/userPreferences'
import type { UserPreferences } from '@/utils/userPreferences'
import { DEFAULT_PREFERENCES } from '@/utils/userPreferences'

export function useUserPreferences() {
	const { data: preferences = DEFAULT_PREFERENCES } = useUserPreferencesQuery()
	const updatePreferenceMutation = useUpdatePreferenceMutation()
	const updateViewPreferenceMutation = useUpdateViewPreferenceMutation()

	/**
	 * Update a user preference (optimistically)
	 */
	const updatePreference = useCallback(
		<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
			updatePreferenceMutation.mutate({ key, value })
		},
		[updatePreferenceMutation]
	)

	/**
	 * Update a view preference for a specific entity (optimistically)
	 */
	const updateViewPreference = useCallback(
		(entity: keyof UserPreferences['viewPreferences'], view: 'table' | 'cards') => {
			updateViewPreferenceMutation.mutate({ entity, view })
		},
		[updateViewPreferenceMutation]
	)

	/**
	 * Get a specific view preference
	 */
	const getView = useCallback(
		(entity: keyof UserPreferences['viewPreferences']): 'table' | 'cards' => {
			return preferences.viewPreferences[entity]
		},
		[preferences]
	)

	return {
		preferences,
		updatePreference,
		updateViewPreference,
		getView,
		isLoading: updatePreferenceMutation.isPending || updateViewPreferenceMutation.isPending,
	}
}
